import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Users, Merge, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const MergeClientsDialog = ({ open, onOpenChange, onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keepByGroup, setKeepByGroup] = useState({});
  const [mergingId, setMergingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clients/duplicates');
      const gs = res.data?.groups || [];
      setGroups(gs);
      const defaults = {};
      gs.forEach((g) => { defaults[g.group_id] = g.clients[0]?.user_id; });
      setKeepByGroup(defaults);
    } catch (err) {
      toast.error('Erro ao carregar duplicados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const doMerge = async (group) => {
    const keepId = keepByGroup[group.group_id];
    const mergeIds = group.clients.map((c) => c.user_id).filter((id) => id !== keepId);
    if (!keepId || mergeIds.length === 0) return;
    setMergingId(group.group_id);
    try {
      const res = await api.post('/clients/merge', { keep_id: keepId, merge_ids: mergeIds });
      toast.success(`Mesclados ${res.data.merged} cadastro(s) · ${res.data.projects_moved} projeto(s) transferido(s)`);
      setGroups((prev) => prev.filter((g) => g.group_id !== group.group_id));
      onMerged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao mesclar');
    } finally {
      setMergingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" />
            Revisar cadastros duplicados
          </DialogTitle>
          <DialogDescription>
            Em cada grupo, escolha qual cadastro manter. Ao confirmar, os projetos e demais registros dos cadastros repetidos passam para o cadastro mantido. Nada é apagado sem sua confirmação.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-800" />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center" data-testid="no-duplicates">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            <p className="text-gray-700 font-medium">Nenhum cadastro duplicado encontrado</p>
            <p className="text-sm text-gray-500">Todos os clientes têm nomes distintos.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => {
              const keepId = keepByGroup[group.group_id];
              const mergeCount = group.clients.filter((c) => c.user_id !== keepId).length;
              const keptName = group.clients.find((c) => c.user_id === keepId)?.full_name;
              return (
                <div key={group.group_id} className="rounded-xl border-2 border-gray-200 p-4" data-testid={`dup-group-${group.group_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      {group.clients.length} cadastros parecidos
                    </h3>
                    <Badge className="bg-amber-100 text-amber-800">Revisar</Badge>
                  </div>

                  <div className="space-y-2">
                    {group.clients.map((c) => {
                      const isKeep = c.user_id === keepId;
                      return (
                        <button
                          key={c.user_id}
                          type="button"
                          onClick={() => setKeepByGroup((p) => ({ ...p, [group.group_id]: c.user_id }))}
                          className={
                            'w-full text-left rounded-lg border-2 p-3 transition-all ' +
                            (isKeep
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 bg-white hover:border-gray-300')
                          }
                          data-testid={`dup-client-${c.user_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 truncate">{c.full_name || '(sem nome)'}</span>
                                <Badge className={c.person_type === 'PJ' ? 'bg-blue-200 text-blue-900 text-[10px]' : 'bg-emerald-200 text-emerald-900 text-[10px]'}>
                                  {c.person_type}
                                </Badge>
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5 space-x-2">
                                {c.email && <span>{c.email}</span>}
                                {c.phone && <span>· {c.phone}</span>}
                                {c.company_name && <span>· {c.company_name}</span>}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {c.project_count} projeto{c.project_count !== 1 ? 's' : ''} vinculado{c.project_count !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <div className="shrink-0">
                              {isKeep ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                                  <Check className="w-4 h-4" /> Manter
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">será mesclado</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-3">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          className="bg-emerald-700 hover:bg-emerald-800"
                          disabled={mergeCount === 0 || mergingId === group.group_id}
                          data-testid={`btn-merge-${group.group_id}`}
                        >
                          <Merge className="w-4 h-4 mr-1" />
                          {mergingId === group.group_id ? 'Mesclando...' : `Mesclar ${mergeCount} no selecionado`}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirmar mesclagem</AlertDialogTitle>
                          <AlertDialogDescription>
                            Manter <strong>{keptName}</strong> e transferir {mergeCount} cadastro(s) para ele.
                            Os projetos e registros vinculados passarão para este cadastro e os repetidos serão removidos.
                            Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => doMerge(group)}
                            className="bg-emerald-700 hover:bg-emerald-800"
                            data-testid={`btn-confirm-merge-${group.group_id}`}
                          >
                            Confirmar mesclagem
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MergeClientsDialog;
